import React, { FC, useState } from 'react';
import { Observer } from 'mobx-react';
import classNames from 'classnames';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

import { Modal } from '@shared/components/Modal';
import { Button, SECONDARY_COLOR_BUTTON_TYPE } from '@shared/components/Button';
import { ExpandButton } from '@shared/components/Button/ExpandButton';
import { InputLabel } from '@shared/components/forms/inputs/InputLabel';
import { ToastMessage } from '@shared/components/Toast';
import { ThemeProps } from '@styles/theme';
import { useIntegrationsUIStore } from '@core/useStores';
import { IProcedureStore } from 'Procedures/stores/Procedure.store';
import FallbackProcedureIcon from '@assets/fallback-procedure-icon.png';

import { LATEST_VERSION_ID } from 'Integrations/domain/ProcedureVersionExportSelection';
import { IntegrationTextSettingField } from 'Integrations/stores/IntegrationsUI.store';
import { messages } from 'Integrations/integrations.messages';
import { useStyles } from './IntegrationsModal.styles';

type IntegrationsModalProps = {
    isOpen: boolean;
    onRequestClose: () => void;
};

type ProcedureRowProps = {
    procedure: IProcedureStore;
    isSelected: boolean;
    selectedVersionId: string;
    onToggle: (isSelected: boolean) => void;
    onVersionChange: (versionId: string) => void;
};

// Text settings, in the order they are shown. `createWbtCourse` is a checkbox
// and is rendered separately.
const TEXT_SETTINGS: Array<{ field: IntegrationTextSettingField, label: string }> = [
    { field: 'split', label: messages['integrations.settings.split'] },
    { field: 'contentFolder', label: messages['integrations.settings.contentFolder'] },
    { field: 'playerTemplate', label: messages['integrations.settings.playerTemplate'] },
    { field: 'contentProvider', label: messages['integrations.settings.contentProvider'] },
    { field: 'contentServer', label: messages['integrations.settings.contentServer'] },
    { field: 'owner', label: messages['integrations.settings.owner'] },
];

const ProcedureRow: FC<ProcedureRowProps> = ({
    procedure,
    isSelected,
    selectedVersionId,
    onToggle,
    onVersionChange,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const versions = _.orderBy(procedure.versions, ['date'], ['desc']);
    const hasVersions = !!versions.length;

    return (
        <div className={classNames(styles.row, { [styles.rowSelected]: isSelected })}>
            <Tooltip title={hasVersions ? '' : messages['integrations.list.noVersions']}>
                <span>
                    <Checkbox
                        checked={isSelected}
                        disabled={!hasVersions}
                        onChange={event => onToggle(event.target.checked)}
                        size='small'
                    />
                </span>
            </Tooltip>

            <img
                className={styles.procedureImage}
                src={procedure.procedurePreview?.src ?? FallbackProcedureIcon}
                alt={procedure.procedurePreview?.description ?? ''}
            />

            <div className={styles.procedureInfo}>
                <span className={styles.procedureName} title={procedure.name}>
                    {procedure.name}
                </span>
                {procedure.type && (
                    <span className={styles.procedureType}>
                        {procedure.type}
                    </span>
                )}
            </div>

            <TextField
                select
                size='small'
                className={classNames(styles.versionPicker, styles.field)}
                // No floating label: the row's value ("Latest", "1.0 — …") says
                // enough, and the shared inputs never float their labels.
                inputProps={{ 'aria-label': messages['integrations.version.label'] }}
                value={hasVersions ? selectedVersionId : ''}
                onChange={event => onVersionChange(event.target.value)}
                disabled={!isSelected || !hasVersions}
                helperText={hasVersions ? undefined : messages['integrations.list.noVersions']}
            >
                <MenuItem value={LATEST_VERSION_ID}>
                    {messages['integrations.version.latest']}
                </MenuItem>
                {versions.map(version => (
                    <MenuItem key={version.id} value={version.id}>
                        {version.version} — {new Date(version.date).toLocaleDateString()}
                    </MenuItem>
                ))}
            </TextField>
        </div>
    );
};

export const IntegrationsModal: FC<IntegrationsModalProps> = ({ isOpen, onRequestClose }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const integrationsUIStore = useIntegrationsUIStore();
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const exportSelected = async () => {
        // No "in progress" toast on purpose: the Export button shows a spinner,
        // and a second toast on completion would read as a double toast.
        const { success, errorMessage } = await integrationsUIStore.exportSelected();

        if (success) {
            toast.success(
                <ToastMessage
                    message={messages['integrations.toast.success']}
                    type='success'
                />
            );
            onRequestClose();
        } else {
            toast.error(
                <ToastMessage
                    message={errorMessage || messages['integrations.toast.error']}
                    type='error'
                />
            );
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
        >
            <div className={styles.header}>
                <Typography variant='h5' color='primary'>
                    {messages['integrations.modal.title']}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                    {messages['integrations.modal.subtitle']}
                </Typography>
            </div>

            <Observer>
                {() => (
                    <>
                        <div className={styles.deploymentRow}>
                            <div className={classNames(styles.fieldGroup, styles.deploymentPicker)}>
                                <InputLabel
                                    label={messages['integrations.deployment.label']}
                                    isRequired
                                />
                                <TextField
                                    select
                                    size='small'
                                    className={styles.field}
                                    value={integrationsUIStore.selectedDeploymentId}
                                    onChange={event => integrationsUIStore.selectDeployment(event.target.value)}
                                    disabled={!integrationsUIStore.deployments.length}
                                    helperText={integrationsUIStore.deployments.length
                                        ? undefined
                                        : messages['integrations.deployment.empty']}
                                >
                                    {integrationsUIStore.deployments.map(deployment => (
                                        <MenuItem key={deployment.id} value={deployment.id}>
                                            {deployment.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <button
                                    type='button'
                                    className={styles.settingsToggle}
                                    onClick={() => setSettingsExpanded(!settingsExpanded)}
                                >
                                    {messages['integrations.settings.title']}
                                </button>
                                <ExpandButton
                                    expand={settingsExpanded}
                                    onClick={() => setSettingsExpanded(!settingsExpanded)}
                                    aria-expanded={settingsExpanded}
                                    aria-label={messages['integrations.settings.title']}
                                    sx={{ p: 0, width: 24, height: 24, ml: 0.5 }}
                                >
                                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                </ExpandButton>
                            </Box>
                        </div>

                        <Collapse in={settingsExpanded}>
                            <div className={styles.settings}>
                                {TEXT_SETTINGS.map(({ field, label }) => (
                                    <div key={field} className={styles.fieldGroup}>
                                        <InputLabel label={label} />
                                        <TextField
                                            size='small'
                                            className={styles.field}
                                            value={integrationsUIStore.settings[field]}
                                            onChange={event =>
                                                integrationsUIStore.setTextSetting(field, event.target.value)}
                                        />
                                    </div>
                                ))}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={integrationsUIStore.settings.createWbtCourse}
                                            onChange={event =>
                                                integrationsUIStore.toggleCreateWbtCourse(event.target.checked)}
                                        />
                                    }
                                    label={
                                        <span className={styles.checkboxLabel}>
                                            {messages['integrations.settings.createWbtCourse']}
                                        </span>
                                    }
                                />
                            </div>
                        </Collapse>

                        <div className={styles.toolbar}>
                            <TextField
                                size='small'
                                className={classNames(styles.searchField, styles.field)}
                                placeholder={messages['integrations.search.placeholder']}
                                value={integrationsUIStore.searchText}
                                onChange={event => integrationsUIStore.setSearchText(event.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon fontSize='small' />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {!!integrationsUIStore.selectedCount && (
                                    <Chip
                                        size='small'
                                        color='primary'
                                        variant='outlined'
                                        label={`${integrationsUIStore.selectedCount} ${messages['integrations.selection.suffix']}`}
                                    />
                                )}
                                <Button
                                    variant='text'
                                    color={SECONDARY_COLOR_BUTTON_TYPE}
                                    onClick={() => integrationsUIStore.toggleAllFiltered(
                                        !integrationsUIStore.areAllSelectableSelected
                                    )}
                                    disabled={!integrationsUIStore.selectableFilteredProcedures.length}
                                >
                                    {integrationsUIStore.areAllSelectableSelected
                                        ? messages['integrations.list.clearSelection']
                                        : messages['integrations.list.selectAll']}
                                </Button>
                            </Box>
                        </div>

                        {integrationsUIStore.isLoading ? (
                            <div className={styles.placeholder}>
                                <CircularProgress size={28} />
                            </div>
                        ) : !integrationsUIStore.procedures.length ? (
                            <div className={styles.placeholder}>
                                <Typography variant='body2' color='textSecondary'>
                                    {messages['integrations.list.empty']}
                                </Typography>
                            </div>
                        ) : !integrationsUIStore.filteredProcedures.length ? (
                            <div className={styles.placeholder}>
                                <Typography variant='body2' color='textSecondary'>
                                    {messages['integrations.list.noResults']}
                                </Typography>
                            </div>
                        ) : (
                            <div className={styles.list}>
                                {integrationsUIStore.filteredProcedures.map(procedure => (
                                    <ProcedureRow
                                        key={procedure.id}
                                        procedure={procedure}
                                        isSelected={integrationsUIStore.isSelected(procedure.id)}
                                        selectedVersionId={integrationsUIStore.selectedVersion(procedure.id)}
                                        onToggle={isSelected =>
                                            integrationsUIStore.toggleProcedure(procedure.id, isSelected)}
                                        onVersionChange={versionId =>
                                            integrationsUIStore.setProcedureVersion(procedure.id, versionId)}
                                    />
                                ))}
                            </div>
                        )}

                        <div className={styles.footer}>
                            <Button color={SECONDARY_COLOR_BUTTON_TYPE} onClick={onRequestClose}>
                                {messages['integrations.cancel.button']}
                            </Button>
                            <Button
                                onClick={exportSelected}
                                disabled={!integrationsUIStore.canExport}
                                loading={integrationsUIStore.isExporting}
                            >
                                {messages['integrations.export.button']}
                            </Button>
                        </div>
                    </>
                )}
            </Observer>
        </Modal>
    );
};
